// src/admin/pages/TenantManagement.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../config/firebase';
import { collection, getDocs, addDoc, doc, updateDoc,deleteDoc} from 'firebase/firestore';
import { showToast, CustomToastContainer } from '../../admin/components/CustomToast';
import '../../styles/TenantManage.css';

const TenantManagement = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [newTenant, setNewTenant] = useState({
    name: '',
    subscriptionPlan: 'free',
    status: 'active'
  });
  const [editingTenant, setEditingTenant] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const tenantsSnapshot = await getDocs(collection(db, 'tenants'));
        const tenantsData = tenantsSnapshot.docs.map(d => {
          const data = d.data();
          // Ensure status has a default value if undefined
          return { 
            id: d.id, 
            ...data,
            status: data.status || 'active' // Default to 'active' if status is undefined
          };
        });
        setTenants(tenantsData);
        setLoading(false);
      } catch (error) {
        showToast('Failed to fetch tenants', 'error');
        setLoading(false);
      }
    };
    fetchTenants();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      setUsers(usersSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    fetchUsers();
  }, []);

  const getAdminLimit = (plan, adminType) => {
    if (plan === 'free') {
      return adminType === 'global' ? 1 : 1;
    } else if (plan === 'basic') {
      return adminType === 'global' ? 3 : 5;
    } else if (plan === 'premium') {
      return 'unlimited';
    }
    return 0;
  };


  const handleCreateTenant = async () => {
    if (!newTenant.name.trim()) {
      showToast('Tenant name is required', 'error');
      return;
    }
  
    if (!newTenant.expiryDate) {
      showToast('Subscription end date is required', 'error');
      return;
    }
  
    try {
      await addDoc(collection(db, 'tenants'), {
        ...newTenant,
        name: newTenant.name.trim(),
        SPOCname: newTenant.SPOCname.trim(),
        emailid: newTenant.emailid.trim(),
        createdAt: new Date(),
        expiryDate: new Date(newTenant.expiryDate), // Ensure it's a Date object
        poNumber: newTenant.poNumber || null,
        invoiceNumber: newTenant.invoiceNumber || null,
        status: 'active',
        globalAdminLimit: getAdminLimit(newTenant.subscriptionPlan, 'global'),
        locationAdminLimit: getAdminLimit(newTenant.subscriptionPlan, 'location'),
        globalAdminCount: 0,
        locationAdminCount: 0
      });
  
      showToast('Tenant created successfully');
      setNewTenant({
        name: '',
        subscriptionPlan: 'free',
        expiryDate: null,
        poNumber: '',
        invoiceNumber: ''
      });
  
      // Refresh tenants list with proper date handling
      const tenantsSnapshot = await getDocs(collection(db, 'tenants'));
      setTenants(tenantsSnapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          createdAt: safeConvertToDate(data.createdAt),
          expiryDate: safeConvertToDate(data.expiryDate),
          status: data.status || 'active'
        };
      }));
    } catch (error) {
      console.error('Error creating tenant:', error);
      showToast('Failed to create tenant', 'error');
    }
  };
  
  const handleEditTenant = (tenant) => {
    setEditingTenant({
      ...tenant,
      status: tenant.status || 'active',
      // Ensure dates are properly handled
      expiryDate: safeConvertToDate(tenant.expiryDate),
      createdAt: safeConvertToDate(tenant.createdAt)
    });
  };
  
  const handleUpdateTenant = async () => {
    if (!editingTenant?.name?.trim()) {
      showToast('Tenant name is required', 'error');
      return;
    }
  
    if (!editingTenant.expiryDate) {
      showToast('Subscription end date is required', 'error');
      return;
    }
  
    try {
      const tenantRef = doc(db, 'tenants', editingTenant.id);
      await updateDoc(tenantRef, {
        name: editingTenant.name.trim(),
        SPOCname: editingTenant.SPOCname.trim(),
        subscriptionPlan: editingTenant.subscriptionPlan,
        emailid: editingTenant.emailid.trim(),
        status: editingTenant.status || 'active',
        expiryDate: new Date(editingTenant.expiryDate), // Ensure it's a Date object
        poNumber: editingTenant.poNumber || null,
        invoiceNumber: editingTenant.invoiceNumber || null,
        globalAdminLimit: getAdminLimit(editingTenant.subscriptionPlan, 'global'),
        locationAdminLimit: getAdminLimit(editingTenant.subscriptionPlan, 'location'),
        globalAdminCount: editingTenant.globalAdminCount || 0,
        locationAdminCount: editingTenant.locationAdminCount || 0,
        createdAt: editingTenant.createdAt || new Date()
      });
  
      showToast('Tenant updated successfully');
      setEditingTenant(null);
      
      // Refresh tenants with proper date handling
      const tenantsSnapshot = await getDocs(collection(db, 'tenants'));
      setTenants(tenantsSnapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          createdAt: safeConvertToDate(data.createdAt),
          expiryDate: safeConvertToDate(data.expiryDate),
          status: data.status || 'active'
        };
      }));
    } catch (error) {
      console.error('Error updating tenant:', error);
      showToast('Failed to update tenant', 'error');
    }
  };
  
  // Helper function to safely convert Firestore Timestamps to Date objects
  const safeConvertToDate = (dateValue) => {
    if (!dateValue) return null;
    if (typeof dateValue.toDate === 'function') {
      return dateValue.toDate(); // Firestore Timestamp
    }
    if (dateValue instanceof Date) {
      return dateValue; // Already a Date object
    }
    if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      return new Date(dateValue); // Convert from string or timestamp
    }
    return null;
  };

const handleDeleteTenant = async (tenantId) => {
  if (!window.confirm('Are you sure you want to delete this tenant? This action cannot be undone.')) {
    return;
  }

  try {
    // 1. Delete ONLY the tenant document.
    //    Cloud Function onTenantDelete will clean up everything else.
    await deleteDoc(doc(db, 'tenants', tenantId));

    showToast('Deletion initiated – tenant and all related data will be removed shortly');

    // 2. Refresh the list in the UI
    const tenantsSnapshot = await getDocs(collection(db, 'tenants'));
    setTenants(tenantsSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  } catch (error) {
    console.error('Error deleting tenant:', error);
    showToast('Failed to delete tenant', 'error');
  }
};


  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  const handleManageSubscription = (tenantId) => {
    navigate(`/admin/subscription/${tenantId}`);
  };

  if (loading) return <div>Loading tenants...</div>;

  return (
    <div className="tenant-management">
      <CustomToastContainer />
      <div className="page-header">
        <button 
          onClick={() => navigate('/admin')} 
          className="back-button"
        >
          &larr; Back
        </button>
        <h1 className="header-center">Tenant Management</h1>
      </div>
      
      <div className="create-tenant">
        <h2>{editingTenant ? 'Edit Tenant' : 'Create New Tenant'}</h2>
        
        {/* First row - two fields */}
        <div className="form-row">
          <div className="form-group">
            <label>Tenant Name*</label>
            <input
              type="text"
              value={editingTenant ? editingTenant.name : newTenant.name}
              onChange={(e) => editingTenant 
                ? setEditingTenant({...editingTenant, name: e.target.value})
                : setNewTenant({...newTenant, name: e.target.value})
              }
              required
            />
          </div>
          <div className="form-group">
            <label>SPOC Name*</label>
            <input
              type="text"
              value={editingTenant ? editingTenant.SPOCname : newTenant.SPOCname}
              onChange={(e) => editingTenant 
                ? setEditingTenant({...editingTenant, SPOCname: e.target.value})
                : setNewTenant({...newTenant, SPOCname: e.target.value})
              }
              required
            />
          </div>

          <div className="form-group">
            <label>Email Id*</label>
            <input
              type="text"
              value={editingTenant ? editingTenant.emailid : newTenant.emailid}
              onChange={(e) => editingTenant 
                ? setEditingTenant({...editingTenant, emailid: e.target.value})
                : setNewTenant({...newTenant, emailid: e.target.value})
              }
              required
            />
          </div>

          <div className="form-group">
            <label>Subscription Plan*</label>
            <select
              value={editingTenant ? editingTenant.subscriptionPlan : newTenant.subscriptionPlan}
              onChange={(e) => editingTenant
                ? setEditingTenant({...editingTenant, subscriptionPlan: e.target.value})
                : setNewTenant({...newTenant, subscriptionPlan: e.target.value})
              }
            >
              <option value="free">Free</option>
              <option value="basic">Basic</option>
              <option value="premium">Premium</option>
            </select>
          </div>
        </div>
        
        {/* Second row - two fields */}
        <div className="form-row">
          <div className="form-group">
            <label>Subscription End Date*</label>
            <input
              type="date"
              value={editingTenant ? formatDateForInput(editingTenant.expiryDate) : newTenant.expiryDate ? formatDateForInput(newTenant.expiryDate) : ''}
              onChange={(e) => {
                const date = e.target.value ? new Date(e.target.value) : null;
                editingTenant
                  ? setEditingTenant({...editingTenant, expiryDate: date})
                  : setNewTenant({...newTenant, expiryDate: date})
              }}
              required
            />
          </div>
          <div className="form-group">
            <label>Status</label>
            <select
              value={editingTenant ? editingTenant.status : newTenant.status || 'active'}
              onChange={(e) => editingTenant
                ? setEditingTenant({...editingTenant, status: e.target.value})
                : setNewTenant({...newTenant, status: e.target.value})
              }
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
        
        {/* Third row - two fields */}
        <div className="form-row">
          <div className="form-group">
            <label>PO Number</label>
            <input
              type="text"
              placeholder="Purchase Order Number"
              value={editingTenant ? editingTenant.poNumber || '' : newTenant.poNumber || ''}
              onChange={(e) => editingTenant
                ? setEditingTenant({...editingTenant, poNumber: e.target.value})
                : setNewTenant({...newTenant, poNumber: e.target.value})
              }
            />
          </div>
          <div className="form-group">
            <label>Invoice Number</label>
            <input
              type="text"
              placeholder="Invoice Number"
              value={editingTenant ? editingTenant.invoiceNumber || '' : newTenant.invoiceNumber || ''}
              onChange={(e) => editingTenant
                ? setEditingTenant({...editingTenant, invoiceNumber: e.target.value})
                : setNewTenant({...newTenant, invoiceNumber: e.target.value})
              }
            />
          </div>
        </div>
        
        {/* Action buttons */}
        <div className="form-actions">
          {editingTenant ? (
            <>
              <button className='update-button' onClick={handleUpdateTenant}>Update Tenant</button>
              <button className="cancel-button" onClick={() => setEditingTenant(null)}>Cancel</button>
            </>
          ) : (
            <button className='create-button' onClick={handleCreateTenant}>Create Tenant</button>
          )}
        </div>
      </div>

      <div className="tenants-list">
        <h2>All Tenants</h2>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Global Admins</th>
              <th>Location Admins</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
          {tenants.map(tenant => {
            // Calculate counts for the current tenant
            const globalAdminCount = users.filter(u => 
              u.tenantId === tenant.id && u.role === 'global_admin'
            ).length;

            const locationUserCount = users.filter(u => 
              u.tenantId === tenant.id && (u.role === 'location_admin' || u.role === 'location_user')
            ).length;

            return (
              <tr key={tenant.id}>
                <td>{tenant.name}</td>
                <td>{tenant.subscriptionPlan}</td>
                <td className={`status-${tenant.status || 'active'}`}>
                  {tenant.status || 'Active'}
                </td>
                <td>
                  {tenant.globalAdminLimit === 'unlimited' 
                    ? 'Unlimited' 
                    : `${globalAdminCount}/${tenant.globalAdminLimit}`}
                </td>
                <td>
                  {tenant.locationAdminLimit === 'unlimited' 
                    ? 'Unlimited' 
                    : `${locationUserCount}/${tenant.locationAdminLimit}`}
                </td>
                <td>
                  <button className='edit-button' onClick={() => handleEditTenant(tenant)}>Edit</button>
                  <button className='manage-button' onClick={() => handleManageSubscription(tenant.id)}>Manage Subscription</button>
                  <button className='delete-button' onClick={() => handleDeleteTenant(tenant.id)}>Delete</button>
                </td>
              </tr>
            );
          })}
        </tbody>
        </table>
      </div>
    </div>
  );
};

export default TenantManagement;